import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

const TICK_MS = 150;
const DURATION_MS = 4000;
const DRIFT_PER_SEC = 0.05;
const DROP_SNAP_RATIO = 0.15;
const ODO_TRANSITION_MS = 280;

function useInterpolatedAmount(target, durationMs = DURATION_MS, tickMs = TICK_MS, driftPerSec = DRIFT_PER_SEC) {
  const initial = () => (
    Number.isFinite(target)
      ? Math.max(0, target - (durationMs / 1000) * driftPerSec)
      : null
  );
  const [display, setDisplay] = useState(initial);
  const currentRef = useRef(initial());

  useEffect(() => {
    if (!Number.isFinite(target)) return undefined;

    if (currentRef.current == null || Number.isNaN(currentRef.current)) {
      currentRef.current = Math.max(0, target - (durationMs / 1000) * driftPerSec);
    }

    const from = currentRef.current;
    const drop = from - target;
    if (drop > 0 && from > 0 && drop / from >= DROP_SNAP_RATIO) {
      currentRef.current = target;
      setDisplay(target);
      return undefined;
    }

    const delta = target - from;
    const started = performance.now();

    const tick = () => {
      const elapsed = performance.now() - started;
      const progress = Math.min(elapsed / durationMs, 1);
      const overtime = Math.max(elapsed - durationMs, 0);
      const next = progress < 1
        ? from + delta * progress
        : target + (overtime / 1000) * driftPerSec;
      currentRef.current = next;
      setDisplay(next);
    };

    tick();
    const id = window.setInterval(tick, tickMs);
    return () => window.clearInterval(id);
  }, [target, durationMs, tickMs, driftPerSec]);

  return display;
}

function toDigits(value, locale) {
  if (value == null || Number.isNaN(Number(value))) return null;
  return Number(value).toLocaleString(locale === 'tr' ? 'tr-TR' : 'en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).split('');
}

const ODO_STRIP = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9];

/**
 * Classic odometer digit: 0–9 ribbon, slides to the active digit.
 * Avoids the old from/to stack that glitched when values updated mid-transition.
 */
function DigitColumn({ digit }) {
  const d = Math.max(0, Math.min(9, Number(digit) || 0));
  const [offset, setOffset] = useState(d);
  const [animate, setAnimate] = useState(false);
  const mounted = useRef(false);
  const pending = useRef(d);

  useLayoutEffect(() => {
    pending.current = d;
    if (!mounted.current) {
      mounted.current = true;
      setOffset(d);
      setAnimate(false);
      return undefined;
    }
    // Snap without animation when jumping more than one step in the same frame budget
    // (length remounts handle big format changes).
    setAnimate(false);
    const id = window.requestAnimationFrame(() => {
      setAnimate(true);
      setOffset(pending.current);
    });
    return () => window.cancelAnimationFrame(id);
  }, [d]);

  return (
    <span className="roll-amount__root">
      <span className="roll-amount__clip">
        <span
          className={`roll-amount__col roll-amount__col--odo${animate ? ' roll-amount__col--animated' : ''}`}
          style={{ transform: `translateY(-${offset}em)` }}
        >
          {ODO_STRIP.map((n) => (
            <span key={n} className="roll-amount__digit">{n}</span>
          ))}
        </span>
      </span>
    </span>
  );
}

export function useJackpotDisplay(value) {
  return useInterpolatedAmount(value == null ? NaN : Number(value));
}

export function useJackpotHitFlash(data, durationMs = 2400, detectWindowMs = 20000) {
  const [flash, setFlash] = useState(null);
  const seen = useRef(new Set());

  useEffect(() => {
    if (!data) return undefined;
    const hit = data.recentHit;
    const tierKeys = ['spades', 'hearts', 'diamonds', 'clubs'];
    let next = null;
    if (hit?.id && hit?.tier && Number.isFinite(Number(hit.amount))) {
      const key = `rh:${hit.id}`;
      if (!seen.current.has(key)) {
        seen.current.add(key);
        next = {
          id: hit.id,
          tier: hit.tier,
          amount: Number(hit.amount),
          until: Date.now() + durationMs,
        };
      }
    }
    if (!next) {
      for (const tier of tierKeys) {
        const row = data[tier];
        if (!row?.hitId || !Number.isFinite(Number(row.hitAmount))) continue;
        const key = `t:${row.hitId}`;
        if (seen.current.has(key)) continue;
        const age = row.hitAt ? Date.now() - new Date(row.hitAt).getTime() : Number.POSITIVE_INFINITY;
        if (row.exploding || (Number.isFinite(age) && age >= 0 && age < detectWindowMs)) {
          seen.current.add(key);
          next = {
            id: row.hitId,
            tier,
            amount: Number(row.hitAmount),
            until: Date.now() + durationMs,
          };
          break;
        }
      }
    }
    if (!next) return undefined;
    setFlash(next);
    const left = Math.max(0, next.until - Date.now());
    const timer = window.setTimeout(() => {
      setFlash((cur) => (cur && cur.id === next.id ? null : cur));
    }, left);
    return () => window.clearTimeout(timer);
  }, [data, durationMs, detectWindowMs]);

  return flash;
}

export function AmountLoader({ className = '', size = 'md' }) {
  return (
    <span
      className={`amount-loader amount-loader--${size}${className ? ` ${className}` : ''}`}
      aria-hidden="true"
      role="presentation"
    >
      <span className="amount-loader__ring" />
    </span>
  );
}

export function isAmountReady(value) {
  return value != null && Number.isFinite(Number(value));
}

export default function RollingAmount({ value, locale, className = '', loadingSize = 'md' }) {
  const chars = useMemo(() => toDigits(value, locale), [value, locale]);
  if (!chars) {
    return <AmountLoader className={className} size={loadingSize} />;
  }

  return (
    <span className={`roll-amount ${className}`.trim()} data-text={chars.join('')}>
      {chars.map((char, i) => (
        /\d/.test(char) ? (
          <DigitColumn key={`d-${i}-${chars.length}`} digit={Number(char)} />
        ) : (
          <span key={`s-${i}-${chars.length}`} className="roll-amount__sep">{char}</span>
        )
      ))}
    </span>
  );
}

export { ODO_TRANSITION_MS };
